import { app } from "@/main";
import store from "@/store";
import { errorTable } from "@/constants";
import { unpause } from "./requests/subscription";
import UpgradePlanModal from "@/components/global/modals/upgrade_plan_modal.vue";
import { mergeUserData } from "@/utils/userUtils";
import i18n from "@/i18n/i18n";

const ignoredUrls = [
  "/login",
  "/register",
  "/forgot-password",
  "/assignments/files",
  "/rubrics/ai-generated/by-upload/",
]; // show error directly on page (Alert)

const trackAndRedirectToUpgrade = async (reason) => {
  const accountProvider = app.$children[0].$children[0].accountProvider;
  const currentPlan = accountProvider.subscription?.plan;
  await app.$egModal.hideAll();
  app.$egModal.show("upgrade-plan-modal", {
    component: UpgradePlanModal,
    props: {
      currentPlan,
    },
  });
  app.$trackEvent("upgrade_clicked", { reason });
};

export default function errorHandler(error) {
  const url = error.config?.url;
  if (
    error.config?.silent ||
    (url &&
      (ignoredUrls.includes(url) ||
        url.startsWith("/rubrics/ai-generated/by-upload"))) // display server error only
  ) {
    return Promise.reject(error);
  }

  const status = error.response?.status;

  if (status === 401) {
    store.dispatch("logout");
    app.$getClerk()?.signOut();
    location.pathname = "/login?ref=expired";
  } else {
    const id = `error-toast-${Date.now()}`;
    const code = error.response?.data?.code;
    let message =
      error?.response?.data?.errors?.[0]?.message ||
      error?.response?.data?.error ||
      i18n.t("errors.genericError");
    let title = i18n.t("common.error");

    if (code) {
      const h = app.$createElement;
      switch (code) {
        case "pausedLimitsExceeded": {
          message = [
            h("p", { class: ["text-center", "mb-0"] }, [
              i18n.t(errorTable["pausedLimitsExceeded"]),
              h("br"),
              h(
                "eg-button",
                {
                  class: ["mt-2"],
                  props: { type: "primary" },
                  on: {
                    click: async () => {
                      await unpause(app);
                      app?.$bvToast?.hide(id);
                    },
                  },
                },
                i18n.t("errors.resume")
              ),
            ]),
          ];
          break;
        }
        case "planWordLimitsExceeded": {
          message = [
            h("p", { class: ["text-center", "mb-0"] }, [
              i18n.t(errorTable["planWordLimitsExceeded"]),
              h("br"),
              h(
                "eg-button",
                {
                  class: ["mt-2"],
                  props: { type: "primary" },
                  on: {
                    click: () => {
                      trackAndRedirectToUpgrade(code);
                      app?.$bvToast?.hide(id);
                    },
                  },
                },
                i18n.t("errors.clickToUpgrade")
              ),
            ]),
          ];
          break;
        }
        case "planAdvancedRubricEssaysLimitExceeded": {
          message = [
            h("p", { class: ["text-center", "mb-0"] }, [
              i18n.t(errorTable["planAdvancedRubricEssaysLimitExceeded"]),
              h("br"),
              h(
                "eg-button",
                {
                  class: ["mt-2"],
                  props: { type: "primary" },
                  on: {
                    click: () => {
                      trackAndRedirectToUpgrade(code);
                      app?.$bvToast?.hide(id);
                    },
                  },
                },
                i18n.t("errors.clickToUpgrade")
              ),
            ]),
          ];
          break;
        }
        case "planEssayLimitsExceeded": {
          message = [
            h("p", { class: ["text-center", "mb-0"] }, [
              i18n.t(errorTable["planEssayLimitsExceeded"]),
              h("br"),
              h(
                "eg-button",
                {
                  class: ["mt-2"],
                  props: { type: "primary" },
                  on: {
                    click: () => {
                      trackAndRedirectToUpgrade(code);
                      app?.$bvToast?.hide(id);
                    },
                  },
                },
                i18n.t("errors.clickToUpgrade")
              ),
            ]),
          ];
          break;
        }
        case "missedPermissions":
          app.$store.dispatch("setUser", {
            ...mergeUserData(app, app.$store.state.user),
            isGCAvailable: false,
          });

          title = i18n.t("errors.googlePermissionsNeeded");
          message = i18n.t(errorTable["missedPermissions"]);
          break;
        case "badGoogleResponse":
          message = i18n.t(errorTable["badGoogleResponse"]);
          break;

        default:
          message = error.response?.data?.message || message;
          break;
      }
    }

    // Check if app and app.$bvToast are available before calling toast
    if (app && app.$bvToast) {
      app.$bvToast.toast(message, {
        id,
        title,
        variant: "danger",
      });
    } else {
      // For test environments or when app is not available
      console.error("Error:", title, message);
    }
  }

  return Promise.reject(error);
}
